<template>
	<div
		v-if="processedItems?.length && isEventActive"
		class="rewards-grid"
		:class="`rewards-grid--count-${count} rewards-grid--${context}`"
	>
		<div
			v-for="(item, idx) in processedItems"
			:key="idx"
			class="rewards-grid__item"
		>
			<Tooltip
				v-if="item"
				:heading="
					eventResourceHeadings[item.key]
						? translate(eventResourceHeadings[item.key])
						: null
				"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<span class="rewards-grid__pre-title">
						{{ translate("event.event") }}
					</span>

					<div
						v-if="
							Object.values(EventUpgradesEnum).includes(
								item.key as EventUpgradesEnum,
							)
						"
						class="rewards-grid__icon"
						:class="`${item?.attributes?.icon}-44`"
					/>
					<div
						v-else
						class="rewards-grid__icon"
						:class="iconClassName(item.key, 44)"
					/>

					<div class="rewards-grid__label">
						<div
							v-if="
								Object.values(EventUpgradesEnum).includes(
									item.key as EventUpgradesEnum,
								)
							"
							class="computed-value"
						>
							<span v-if="item?.attributes?.income">
								{{
									`+${item.attributes.income}/${TICK_DURATION}s`
								}}
							</span>
							<span v-if="item?.attributes?.percentage">
								{{ `+${item.attributes.percentage}%` }}
							</span>
							<span v-if="item?.attributes?.value">
								{{ `+${item.attributes.value}` }}
							</span>
							<div
								v-if="
									Object.values(EventUpgradesEnum).includes(
										item.key as EventUpgradesEnum,
									)
								"
								class="rewards-grid__icon"
								:class="`${item?.attributes?.icon}-${dynamicSize.priceIcon}`"
							/>
						</div>
						<AppPrice
							v-else
							:amount="item.qty"
							:currency="item.key as ResourceType"
							:fontSize="dynamicSize.price"
							:iconSize="dynamicSize.priceIcon"
							:resourceChangeSign="
								ResourceChangeHighlight.POSITIVE
							"
							:postfix="item?.attributes?.percentage ? '%' : null"
						/>
					</div>
				</template>
				<template #tooltip>
					<span
						v-if="eventResourceDescriptions[item.key]"
						class="reward-tooltip"
					>
						{{
							replacePlaceholder(
								replacePlaceholder(
									replacePlaceholder(
										translate(
											eventResourceDescriptions[
												item.key
											] ?? "",
										),
										"{percent}",
										`${item?.attributes?.percentage ?? 0}%`,
									),
									"{number}",
									+(
										item?.attributes?.income ??
										item?.attributes?.value ??
										0
									),
								),
								"{building}",
								translate(`${item?.attributes?.building}.name`),
							)
						}}
					</span>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import Tooltip from "@/components/Tooltip.vue";
import { TICK_DURATION } from "@/config/constants";
import { useEventStore } from "@/event";
import type { ResourceType } from "@/game/entities/config/resources";
import type { UpgradeItem } from "@/game/entities/config/upgrades";
import {
	EventUpgradeIds,
	eventUpgrades,
} from "@/game/entities/config/upgrades/event";
import {
	EventUpgrades as EventUpgradesEnum,
	type EventResourceSlotItem,
} from "@/types/shop";
import {
	Placements,
	ResourceChangeHighlight,
	RewardsGridContext,
} from "@/types/ui";
import { iconClassName } from "@/utils/iconClassName";
import { getNumber } from "@/utils/number";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import {
	getEventResourceDescriptions,
	getEventResourceHeadings,
} from "@/utils/shop/texts";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	items: Array<EventResourceSlotItem> | null;
	context: RewardsGridContext;
}

const { items, context } = defineProps<Props>();

const eventStore = useEventStore();
const { isActive: isEventActive } = storeToRefs(eventStore);

const count = computed((): number => items?.length ?? 0);

const dynamicSize = computed((): Record<string, number> => {
	if (context === RewardsGridContext.CARD) {
		return { icon: 80, price: 12, priceIcon: 14 };
	}
	return { icon: 80, price: 20, priceIcon: 10 };
});

const eventResourceHeadings = computed(
	(): Record<string, string> => getEventResourceHeadings(),
);

const eventResourceDescriptions = computed(
	(): Record<string, string> => getEventResourceDescriptions(),
);

type ExtendedResourceSlotItem = EventResourceSlotItem & Partial<UpgradeItem>;

// For event phantom rewards
const processedItems = computed((): Array<ExtendedResourceSlotItem> => {
	if (!items) return [];

	return items.reduce(
		(
			acc: Array<ExtendedResourceSlotItem>,
			item: EventResourceSlotItem,
		): Array<ExtendedResourceSlotItem> => {
			const upgradeKey = item.key as EventUpgradesEnum;
			const isUpgrade =
				Object.values(EventUpgradesEnum).includes(upgradeKey);

			const isConversionRate = item.key.includes(
				"event_conversion_offers_rate_",
			);

			if (isUpgrade) {
				const upgradeId =
					EventUpgradeIds[upgradeKey as keyof typeof EventUpgradeIds];
				const upgradeData = eventUpgrades.get(upgradeId) ?? null;

				acc.push({
					...item,
					...upgradeData,
				});
			} else if (isConversionRate) {
				acc.push({
					...item,
					qty: getNumber(item.key),
					attributes: {
						percentage: getNumber(item.key), // For text {percent} replacement
					},
				});
			} else {
				acc.push(item);
			}

			return acc;
		},
		[],
	);
});
</script>

<style lang="scss" scoped>
.rewards-grid {
	@include flex-row-center;
	position: relative;
	gap: 0.125rem; // SLIM fix
	z-index: 1;
	padding: 0.625rem 0; // SLIM fix

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__pre-title {
		color: #f4d278;
		font-weight: 800;
		font-size: 0.625rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&__item {
		@include flex-col-center;
		row-gap: 6px;
		justify-items: center;
		width: 100%;
		min-width: 5rem;

		.v-popper {
			width: 100%;
			@include flex-col-center;
		}
	}

	&__label {
		@include flex-row-center;
		position: relative;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		border-width: 0.063rem 0 0.063rem 0;
		border-style: solid;
		border-image-source: linear-gradient(
			to bottom,
			rgba(216, 169, 114, 1),
			rgba(226, 181, 145, 1)
		);
		border-image-slice: 1;
		width: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 1;
			mix-blend-mode: multiply;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			background: rgb(112, 62, 16);
			width: 100%;
			height: 100%;
			content: "";
		}

		.price,
		.computed-value {
			z-index: 1;
			margin: 0 1.375rem; // SLIM fix
		}

		.computed-value {
			@include flex-row-center;
			gap: 0.25rem;
			color: #fff3dd;
			font-weight: 800;
		}
	}
}
</style>
